
import React, { useCallback } from "react";
import { gql, useQuery, useSubscription } from "@apollo/client";
import {
  Transaction,
  FindTransactionsBySessionQuery,
  FindTransactionsBySessionQueryVariables
} from "../../../../schemaTypes";
import { newTransactionSubscription } from "../FindTransactions";

export const findTransactionsBySessionQuery = gql`
  query FindTransactionsBySession(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findTransactionsBySession(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        amount
        charge
        reference
        type
        status
        currency
        transferTypes
        bankPreference{
          id
        }
        offer {
          id
          title
          description
          bannerUrl
          category {
            id
            name
            imgSrc
          }
          offerType
          discountType
          discountValue
          partnerVoucher
          referralUrl
          amount
          status
          usageLimitPerUser
          totalUsageLimit
          startDate
          endDate
          community{
            id
            name
            alias
          }
          redemptions
          revenueGenerated
          clickThroughs
          featured
          createdBy{
            id
            role
          }
          createdAt
          updatedAt
        }
        njoyCode
        partnerVoucher
        user {
          id
          customer {
            firstname
            lastname
          }
        }
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

newTransactionSubscription;

export interface WithFindTransactionsBySession {
  transactions: Transaction[];
  loading: boolean;
  loadMore: () => void;
  hasMoreTransactions: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: FindTransactionsBySessionQueryVariables;
  children: (data: WithFindTransactionsBySession) => React.ReactElement | null;
}

export const FindTransactionsBySession: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindTransactionsBySessionQuery,
    FindTransactionsBySessionQueryVariables
  >(findTransactionsBySessionQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  // Subscription for new transactions (optional, adjust as needed)
  useSubscription(newTransactionSubscription);

  const transactions = data?.findTransactionsBySession?.data || [];
  const page = data?.findTransactionsBySession?.page || variables.page;
  const pageSize = data?.findTransactionsBySession?.pageSize || variables.pageSize;
  const total = data?.findTransactionsBySession?.total || 0;
  const totalPages = data?.findTransactionsBySession?.totalPages || 0;
  const hasMoreTransactions = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreTransactions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findTransactionsBySession) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findTransactionsBySession: {
            ...fetchMoreResult.findTransactionsBySession,
            data: [
              ...(prev?.findTransactionsBySession?.data || []),
              ...(fetchMoreResult.findTransactionsBySession.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreTransactions]);

  return children({
    transactions: transactions as any,
    loading,
    loadMore,
    hasMoreTransactions,
    page,
    pageSize,
    total,
    totalPages,
  });
};